import { useCallback, useRef } from "react";

import { trackEvent } from "@/lib/analytics";
import { nativeBridge } from "@/lib/native";

import { useShowAds } from "./useShowAds";

/** 인터스티셜 최소 간격 (3분) */
const COOLDOWN_MS = 3 * 60 * 1000;

/**
 * 앱 전역 첫인상 보호 플래그.
 *
 * 모듈 레벨에 두어 페이지 전환(훅 재생성)과 무관하게
 * 앱 실행 후 딱 한 번만 스킵한다.
 */
let hasEverAttempted = false;

/**
 * 인터스티셜(전면) 광고를 조건부로 표시하는 훅.
 *
 * - Plus 구독자 또는 웹 환경에서는 항상 no-op.
 * - 마지막 노출로부터 3분 이내면 스킵.
 * - 앱 실행 후 첫 호출은 스킵 (첫인상 보호).
 */
export function useInterstitialAd() {
  const showAds = useShowAds();
  const lastShownRef = useRef(0);

  const maybeShow = useCallback(
    async (source: string) => {
      if (!showAds) {
        return false;
      }

      // 첫인상 보호: 앱 실행 후 첫 인터스티셜은 스킵
      if (!hasEverAttempted) {
        hasEverAttempted = true;

        return false;
      }

      const now = Date.now();

      if (now - lastShownRef.current < COOLDOWN_MS) {
        return false;
      }

      const result = await nativeBridge.ad.showInterstitial();

      if (result.shown) {
        lastShownRef.current = now;
        trackEvent("ad_interstitial_shown", { source });
      }

      return result.shown;
    },
    [showAds],
  );

  return { maybeShow };
}
